import type { Cents } from "../domain/money";
import { cell, type ExportSheet, type ReportIdentity, type ReportModel, type ReportRow, type ReportSection, type ReportValue } from "./reportModel";

/**
 * PROCUREMENT / BOM REPORT (Phase 13, spec §42E).
 *
 * Answers one practical question — "what do we still need to buy?" — from
 * the Phase 9B fulfillment engine, which derives everything from real
 * purchase evidence. Nothing here computes a requirement or a price.
 *
 * The NEED TO PURCHASE variant is deliberately short enough to carry into a
 * store: item, where it goes, how many, roughly what it costs.
 */

export type ProcurementFilter = "all" | "need" | "purchased" | "crew-supplied";

export interface ProcurementReportRow {
  productName: string;
  supplierName: string;
  productUrl: string | null;
  productId: string | null;
  scopeLabels: string[];
  featureNames: string[];
  unit: string;
  purchaseUnit: string | null;
  requiredX100: number;
  requiredUnits: number;
  purchasedUnitsX100: number;
  remainingUnitsX100: number;
  excessUnitsX100: number;
  packageExcessX100: number;
  unitPriceCents: Cents | null;
  estimatedTotalCents: Cents;
  spentCents: Cents;
  status: string;
  statusLabel: string;
  disposition: string | null;
  needsSelection: boolean;
  quantityReviewRequired?: boolean;
  remainingQuantityUnknown?: boolean;
  snapshotNotes?: string[];
}

export interface ProcurementReportInput {
  identity: ReportIdentity;
  rows: ProcurementReportRow[];
  filter: ProcurementFilter;
  groupBy: "supplier" | "room" | "status";
  generatedAt: string;
}

const money = (cents: Cents): ReportValue => ({ kind: "money", cents });
const number = (value: number): ReportValue => ({ kind: "count", value });

export function filterProcurementRows(rows: ProcurementReportRow[], filter: ProcurementFilter): ProcurementReportRow[] {
  switch (filter) {
    case "need":
      return rows.filter((row) => (row.remainingUnitsX100 > 0 || row.quantityReviewRequired || row.remainingQuantityUnknown || row.needsSelection) && row.disposition === null);
    case "purchased":
      return rows.filter((row) => row.purchasedUnitsX100 > 0);
    case "crew-supplied":
      return rows.filter((row) => row.disposition !== null);
    default:
      return rows;
  }
}

function groupKey(row: ProcurementReportRow, groupBy: ProcurementReportInput["groupBy"]): string {
  if (groupBy === "supplier") return row.supplierName || "No supplier selected";
  if (groupBy === "room") return row.scopeLabels[0] ?? "Project-wide";
  return row.statusLabel;
}

export function procurementReport(input: ProcurementReportInput): ReportModel {
  const rows = filterProcurementRows(input.rows, input.filter);
  const shopping = input.filter === "need";
  const groups = new Map<string, ProcurementReportRow[]>();
  for (const row of rows) {
    const key = groupKey(row, input.groupBy);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const sections: ReportSection[] = [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([title, groupRows]) => ({
      title,
      columnLabel: "Item",
      columns: shopping
        ? ["Still needed", "Unit price", "Est. cost"]
        : ["Required", "Purchased", "Remaining", "Est. cost", "Actual"],
      rows: [
        ...groupRows.map((row): ReportRow => {
          const where = row.scopeLabels.join(", ") || "Project-wide";
          const note = [
            where,
            row.featureNames.slice(0, 3).join(" · "),
            row.needsSelection ? "no product selected — generic estimate" : null,
            row.quantityReviewRequired ? "Incompatible purchase units — quantity comparison not confirmed" : row.remainingQuantityUnknown ? "Returned quantity unrecorded — remaining packages not confirmed" : null,
            row.snapshotNotes?.join("; "),
            row.disposition ? dispositionLabel(row.disposition) : null,
            !shopping ? row.statusLabel : null,
            !row.quantityReviewRequired && !row.remainingQuantityUnknown && row.excessUnitsX100 > 0 ? `${units(row.excessUnitsX100)} purchased beyond the requirement` : null,
            !row.quantityReviewRequired && row.packageExcessX100 > 0 ? `${units(row.packageExcessX100)} package excess` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          const label = `${row.productName}${row.supplierName && input.groupBy !== "supplier" ? ` (${row.supplierName})` : ""}`;
          return {
            label,
            note: note || undefined,
            link: row.productUrl ? { url: row.productUrl, text: "View product" } : undefined,
            source: row.productId ? { kind: "product", id: row.productId } : undefined,
            values: shopping
              ? [
                  { kind: "text", text: row.quantityReviewRequired || row.remainingQuantityUnknown ? "Not confirmed" : row.needsSelection ? "Choose product" : `${units(row.remainingUnitsX100)} ${row.purchaseUnit ?? row.unit}` },
                  row.unitPriceCents === null ? { kind: "empty" } : money(row.unitPriceCents),
                  money(row.estimatedTotalCents),
                ]
              : [
                  // With no product there are no purchase units yet, so the
                  // construction requirement is what there is to report.
                  {
                    kind: "text",
                    text: row.quantityReviewRequired ? "Review frozen units" : row.needsSelection
                      ? `${units(row.requiredX100)} ${row.unit}`
                      : `${row.requiredUnits} ${row.purchaseUnit ?? row.unit}`,
                  },
                  row.quantityReviewRequired ? { kind: "text", text: "Not confirmed" } : number(row.purchasedUnitsX100 / 100),
                  row.quantityReviewRequired || row.remainingQuantityUnknown ? { kind: "text", text: "Not confirmed" } : number(row.remainingUnitsX100 / 100),
                  money(row.estimatedTotalCents),
                  money(row.spentCents),
                ],
          };
        }),
        {
          label: `${title} total`,
          emphasis: "subtotal",
          values: shopping
            ? [{ kind: "empty" }, { kind: "empty" }, money(sum(groupRows, (r) => r.estimatedTotalCents))]
            : [
                { kind: "empty" },
                { kind: "empty" },
                { kind: "empty" },
                money(sum(groupRows, (r) => r.estimatedTotalCents)),
                money(sum(groupRows, (r) => r.spentCents)),
              ],
        },
      ],
    }));

  const totalRows: ReportRow[] = [
    { label: "Items listed", values: [number(rows.length)] },
    { label: "Estimated cost", values: [money(sum(rows, (r) => r.estimatedTotalCents))] },
  ];
  if (!shopping) {
    totalRows.push({ label: "Recorded against these items", values: [money(sum(rows, (r) => r.spentCents))] });
  }
  sections.push({ title: "Totals", columnLabel: "", columns: ["Amount"], rows: totalRows });

  const warnings: string[] = [];
  const unselected = rows.filter((row) => row.needsSelection).length;
  if (unselected > 0) {
    warnings.push(
      `${unselected} item(s) have no product selected. Their cost is the generic assembly estimate, not a quoted price.`,
    );
  }
  const overbought = rows.filter((row) => !row.quantityReviewRequired && !row.remainingQuantityUnknown && row.excessUnitsX100 > 0).length;
  if (overbought > 0) warnings.push(`${overbought} item(s) were purchased beyond the requirement; the excess stays visible.`);

  return {
    id: `procurement-${input.filter}`,
    title: shopping ? "Need to purchase" : "Procurement & bill of materials",
    subtitle: filterLabel(input.filter),
    identity: input.identity,
    optionSummary: `${filterLabel(input.filter)} · grouped by ${input.groupBy}`,
    sections,
    warnings,
    disclosures: [
      "Purchased quantities are derived from real purchase records linked to each product — never from an assumption that a purchase happened.",
      "Estimated cost is the project's copy-on-use price snapshot with estimating tax; actual cost is what the accounting records show.",
    ],
    generatedAt: input.generatedAt,
  };
}

/** Machine-readable procurement rows for CSV/XLSX. */
export function procurementExportSheet(rows: ProcurementReportRow[]): ExportSheet {
  return {
    name: "Procurement",
    columns: [
      { key: "product", label: "Product" },
      { key: "supplier", label: "Supplier" },
      { key: "scope", label: "Room / scope" },
      { key: "features", label: "Features" },
      { key: "unit", label: "Purchase unit" },
      { key: "requiredUnits", label: "Required units" },
      { key: "purchasedUnits", label: "Purchased units" },
      { key: "remainingUnits", label: "Remaining units" },
      { key: "packageExcess", label: "Package excess" },
      { key: "purchasedExcess", label: "Purchased excess" },
      { key: "unitPrice", label: "Unit price" },
      { key: "estimated", label: "Estimated extended cost" },
      { key: "actual", label: "Actual purchase cost" },
      { key: "status", label: "Status" },
      { key: "disposition", label: "Disposition" },
      { key: "url", label: "Product URL" },
    ],
    rows: rows.map((row) => [
      cell.text(row.productName),
      cell.text(row.supplierName),
      cell.text(row.scopeLabels.join("; ")),
      cell.text(row.featureNames.join("; ")),
      cell.text(row.quantityReviewRequired ? "Mixed frozen units" : row.purchaseUnit ?? row.unit),
      row.quantityReviewRequired ? cell.text("Not confirmed") : cell.number(row.requiredUnits, 0),
      row.quantityReviewRequired ? cell.text("Not confirmed") : cell.number(row.purchasedUnitsX100 / 100),
      row.quantityReviewRequired || row.remainingQuantityUnknown ? cell.text("Not confirmed") : cell.number(row.remainingUnitsX100 / 100),
      row.quantityReviewRequired ? cell.text("Not confirmed") : cell.number(row.packageExcessX100 / 100),
      row.quantityReviewRequired || row.remainingQuantityUnknown ? cell.text("Not confirmed") : cell.number(row.excessUnitsX100 / 100),
      row.unitPriceCents === null ? cell.empty() : cell.money(row.unitPriceCents),
      cell.money(row.estimatedTotalCents),
      cell.money(row.spentCents),
      cell.text(row.statusLabel),
      cell.text(row.disposition ? dispositionLabel(row.disposition) : ""),
      cell.text(row.productUrl ?? ""),
    ]),
  };
}

function sum(rows: ProcurementReportRow[], pick: (row: ProcurementReportRow) => number): number {
  return rows.reduce((total, row) => total + pick(row), 0);
}

function units(x100: number): string {
  const value = x100 / 100;
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function dispositionLabel(disposition: string): string {
  return disposition === "crew-supplied" ? "Crew supplied" : "Not required";
}

function filterLabel(filter: ProcurementFilter): string {
  switch (filter) {
    case "need":
      return "Still to buy";
    case "purchased":
      return "Purchased";
    case "crew-supplied":
      return "Crew supplied / not required";
    default:
      return "All requirements";
  }
}
